import { Injector } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AppStateFacade } from '@app-state';
import {
    entityParamExtractor,
    IMonitoringRouteParams,
} from '@pages/monitoring/dashboard/widgets/generics/services/entityParamExtractor';
import {
    filterParamExtractor,
    IMonitoringErrorLogsOptions,
    IMonitoringQueryOptions,
} from '@pages/monitoring/dashboard/widgets/generics/services/filterParamExtractor';

import { retryBackoff } from 'backoff-rxjs';
import {
    BehaviorSubject,
    combineLatest,
    merge,
    Observable,
    of,
    Subject,
} from 'rxjs';
import {
    debounceTime,
    delay,
    distinctUntilChanged,
    filter,
    finalize,
    map,
    share,
    startWith,
    switchMap,
    takeUntil,
    tap,
} from 'rxjs/operators';

export const DEFAULT_FETCH_INTERVAL = 5000;

export type IWidgetQueryOptions = IMonitoringRouteParams & IMonitoringQueryOptions & IMonitoringErrorLogsOptions;

export enum LoadingState {
    initial = 'initial',
    update = 'update',
    idle = 'idle',
}

interface LoadEvent {
    status: LoadingState;
    responseTime: number;
}

export const MIN_LOADING_TIME_ANIMATION = 350;

export class WidgetRefreshService<K> {
    public loading$!: Observable<LoadingState>;
    public hasData$ = new BehaviorSubject(false);
    public refreshData$!: Observable<IWidgetQueryOptions>;
    public queryParams$!: Observable<IWidgetQueryOptions>;
    public fetch$ = new Subject();

    private _destroyed$ = new Subject();
    private _route = this._injector.get<ActivatedRoute>(ActivatedRoute);
    private _appState = this._injector.get<AppStateFacade>(AppStateFacade);
    private _loading$ = new BehaviorSubject<LoadEvent>({
        status: LoadingState.initial,
        responseTime: -1,
    });
    private _enqueueFetch$ = new Subject();
    private _hasData: ((_: K) => boolean) | null = null;

    constructor(
        private _injector: Injector,
        hasData: (_: K) => boolean,
        fetchInterval?: number,
    ) {
        fetchInterval = fetchInterval || DEFAULT_FETCH_INTERVAL;

        if (!this._route) { return; }

        this._hasData = hasData;

        this.loading$ = this._loading$.pipe(
            switchMap(ev => of(ev)
                .pipe(
                    delay(ev.responseTime !== -1 &&
                        ev.responseTime < MIN_LOADING_TIME_ANIMATION ?
                        MIN_LOADING_TIME_ANIMATION - ev.responseTime :
                        0,
                    ),
                ),
            ),
            map(ev => ev.status),
            startWith(LoadingState.initial),
            takeUntil(this._destroyed$),
        );

        this.queryParams$ = combineLatest([
            this._route
                .queryParamMap
                .pipe(
                    map(filterParamExtractor),
                ),
            this._route
                .paramMap
                .pipe(
                    map(entityParamExtractor),
                    map(({ entityId }) => entityId),
                    distinctUntilChanged(),
                ),
            this._appState.user.folder.value$,
        ]).pipe(
            // we need to debounce because it's possible to have multiple emissions at the same time because of the OU change
            debounceTime(10),
            map(([queryParams, entityId]) => Object.assign({}, queryParams, { entityId })),
        );

        const refreshTriggers$ = merge(
            this._enqueueFetch$
                .pipe(
                    debounceTime(fetchInterval),
                    // The automatic _enqueueFetch$ should not override a pending request triggered by the user.
                    filter(() => this._loading$.value.status === LoadingState.idle),
                    share(),
                ),
            this.fetch$,
        )
            .pipe(
                startWith(void 0),
            );

        this.refreshData$ =
            combineLatest([
                refreshTriggers$,
                this.queryParams$,
            ])
                .pipe(
                    map(([_, params]) => params),
                    takeUntil(this._destroyed$),
                );
    }

    public destroy() {
        this._destroyed$.next();
    }

    public intercept(
        serviceCallObservable: (_: IWidgetQueryOptions) => Observable<K>,
    ): Observable<K> {
        let timestamp: number;
        let lastOuId: number;

        return this.refreshData$.pipe(
            tap(() => {
                const currentOuId = this._appState.user.folder.value!.id;
                const isSameOuId = lastOuId === currentOuId;

                this._loading$.next({
                    status: this._loading$.value.status !== LoadingState.initial && isSameOuId ?
                        LoadingState.update :
                        LoadingState.initial,
                    responseTime: -1,
                });

                if (!isSameOuId) {
                    lastOuId = currentOuId;
                }
            }),
            tap(() => timestamp = performance.now()),
            switchMap(
                (params) => serviceCallObservable(params)
                    .pipe(
                        finalize(() => this._enqueueFetch$.next()),
                    ),
            ),
            retryBackoff({
                initialInterval: 300,
                maxInterval: 60 * 1000,
            }),
            share(),
            tap(res => this.hasData$.next(this._hasData!(res))),
            tap(() => {
                this._loading$.next({
                    status: LoadingState.idle,
                    responseTime: performance.now() - timestamp,
                });
            }),
            takeUntil(this._destroyed$),
        );
    }
}
